import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Modal from "react-bootstrap/Modal";
import { pageText } from "../../utils/pageText";

export default function SimilaritySearch({ datasetName, model = "dinov2" }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [samples, setSamples] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [threshold, setThreshold] = useState("0.8");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [embeddingsReady, setEmbeddingsReady] = useState(null);
  const feedbackRef = useRef(null);
  const params = () => ({ dataset_name: datasetName, model, requesterToken: Cookies.get("user_token") });
  useEffect(() => {
    if (!open) return;
    let active = true;
    setError("");
    setSamples([]);
    axios.get("/datasets/dino/samples", { params: { dataset_name: datasetName, model,
      requesterToken: Cookies.get("user_token"), page } }).then(({ data }) => {
      if (active) { setSamples(data.samples); setTotal(data.total); setEmbeddingsReady(data.embeddings_ready ?? null); }
    }).catch((err) => { if (active) setError(err.response?.data?.error || err.message); });
    return () => { active = false; };
  }, [open, page, datasetName, model]);
  useEffect(() => {
    if (error || result) feedbackRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [error, result]);
  const search = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await axios.get("/datasets/dino/similar", {
        params: { ...params(), sample_id: selected.id, threshold, limit: 50 }, timeout: 60000 });
      setResult(data);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  };
  return <>
    <button type="button" className="org-btn org-btn-secondary" onClick={() => {
      setOpen(true); setPage(1); setSelected(null); setResult(null);
    }}>{pageText("Search Similar Images")}</button>
    <Modal show={open} onHide={() => { if (!busy) setOpen(false); }} size="lg" centered scrollable>
      <Modal.Header closeButton={!busy}><Modal.Title>{pageText("Search Similar Images")} — {datasetName}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{pageText("Extract features first, then select a reference image. Search is limited to this dataset.")}</p>
        <div className="dinov2-image-grid">
          {samples.map(sample => <button key={sample.id} type="button" disabled={busy}
            aria-pressed={selected?.id === sample.id} aria-label={sample.name}
            className={`dinov2-image-tile ${selected?.id === sample.id ? "selected" : ""}`}
            onClick={() => { setSelected(sample); setResult(null); }}>
            <img src={sample.preview_url} alt={sample.name} loading="lazy" />
            <span>{sample.name}</span>
          </button>)}
        </div>
        <div className="d-flex align-items-center gap-2 my-3">
          <button type="button" disabled={busy || page === 1} onClick={() => setPage(page - 1)}>{pageText("Previous")}</button>
          <span>{page} / {Math.max(1, Math.ceil(total / 24))}</span>
          <button type="button" disabled={busy || page * 24 >= total} onClick={() => setPage(page + 1)}>{pageText("Next")}</button>
        </div>
        {embeddingsReady === false && <p role="status" className="text-warning">{pageText("No image embeddings yet. Close this window, click Start under Embedding, and wait for the task to finish.")}</p>}
        {selected && <p>{pageText("Reference Image")}: {selected.name}</p>}
        <label>{pageText("Similarity Threshold")}
          <input type="number" min="-1" max="1" step="0.01" value={threshold} disabled={busy}
            onChange={e => { setThreshold(e.target.value); setResult(null); }} />
        </label>
        <button type="button" className="org-btn org-btn-primary ms-2" onClick={search}
          disabled={busy || !selected || threshold === "" || !Number.isFinite(Number(threshold)) || Number(threshold) < -1 || Number(threshold) > 1}>
          {pageText(busy ? "Searching…" : "Search")}</button>
        <div ref={feedbackRef} aria-live="polite">
        {error && <p role="alert" className="text-danger mt-2">{pageText(error)}</p>}
        {result && result.total_matches === 0 && <p>{pageText("No similar images meet this threshold. Try a lower threshold.")}</p>}
        {result && <section className="mt-3" aria-label={pageText("Similar Images")}>
          <p>{pageText("Matches")}: {result.total_matches} ({pageText("Showing up to 50 results")})</p>
          <div className="dinov2-image-grid">{result.matches.map(sample => <div key={sample.id} className="dinov2-image-tile">
            <img src={sample.preview_url} alt={sample.name} loading="lazy" />
            <span>{sample.name}</span><strong>{sample.similarity.toFixed(4)}</strong>
          </div>)}</div>
        </section>}
        </div>
      </Modal.Body>
    </Modal>
  </>;
}
